import ArrowForward from "@mui/icons-material/ArrowForward";
import Send from "@mui/icons-material/Send";
import { useEffect, useMemo, useRef, useState } from "react";
import ProductCard from "@/components/products/ProductCard";
import { giftFoldEnvelope } from "@/components/redemption/experiential/giftFoldEnvelope";
import RedemptionAddressVerifyModal from "@/components/redemption/RedemptionAddressVerifyModal";
import RedemptionCtaShelf from "@/components/redemption/RedemptionCtaShelf";
import RedemptionDetailsForm from "@/components/redemption/RedemptionDetailsForm";
import RedemptionDetailsHeader from "@/components/redemption/RedemptionDetailsHeader";
import RedemptionReviewCard from "@/components/redemption/RedemptionReviewCard";
import RedemptionSpinner from "@/components/redemption/RedemptionSpinner";
import { GfgButton } from "@/components/ui/GfgButton";
import { AVAILABLE_COUNTRIES } from "@/constants/campaignBuilder";
import { EMPTY_RECIPIENT_FORM, RECIPIENT_EMAIL_RE } from "@/constants/redemption";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useRedeemGift } from "@/hooks/useRedeemGift";
import type { Product } from "@/interfaces/product";
import type {
  AddressParts,
  CustomLocation,
  GiftContext,
  RecipientDetails,
  RecipientFormDraft,
} from "@/interfaces/redemption";
import { addressInputLines, buildReviewAddressLines } from "@/utils/address-helper";
import { getAddressProfile, getRegionOptions, matchRegionValue } from "@/utils/address-profiles";
import { isValidPhone, toE164 } from "@/utils/phone-helper";
import { giftKind, redemptionLinkError } from "@/utils/redemption-helper";
import { verifyUsAddress } from "@/utils/smarty";

// Prototype CTA sizing (`.iv-continue`): 15×40 padding, 12px label, 8px radius.
// GfgButton twMerges className, so these override its base size cleanly.
const CTA_SIZE = "rounded-lg px-10 py-3.75 text-[12px]";
// The paper-plane CTA icon bob (prototype `.iv-send-icon`), stilled for reduced motion.
const SEND_ICON = "animate-send-bob motion-reduce:animate-none";

type Phase = "form" | "verifying" | "review" | "submitting";

interface RedemptionDetailsProps {
  gift: GiftContext;
  /** The chosen gift (full detail) — drives the summary card + gift-type branch. */
  product: Product;
  /** The exact variant chosen in the detail slide-in (what we redeem). */
  variantId: string;
  /** Well Told custom-map location chosen in the slide-in; null for any other gift. */
  customLocation?: CustomLocation | null;
  /** Review "Edit" on the custom location → re-open the gift detail to pick again. */
  onEditLocation?: () => void;
  /** Destination country (ISO-2) from the feed's "Shipping to" picker — fixes the address
   *  profile + the read-only country field. Lifted to Redemption so the feed + form agree. */
  shippingCountry: string;
  /** Back to the gift feed (detail overlay / landing). */
  onBack: () => void;
  /** Redeem succeeded → the flow advances (RFW-8 message + impact). */
  onRedeemed: (contactId?: number) => void;
  /** Submit hit 410 (gift spent after the page loaded) → show the redeemed page, not the generic failure. */
  onAlreadyRedeemed: () => void;
  /** Restore a form the recipient started, left ("Back to gifts"), and returned to. */
  initialDraft?: RecipientFormDraft | null;
  /** Report the latest form on unmount so the draft survives a round-trip to the feed. */
  onDraftChange?: (draft: RecipientFormDraft) => void;
}

/**
 * Recipient Details — reached from the detail slide-in's "Choose this gift". Collects
 * contact info (always) + shipping (physical gifts). Flow:
 *   `form → verifying (US physical: Smarty address check → "did you mean?" modal)
 *         → review → submitting (real POST /:token/redeem) → fold-and-fly → onRedeemed()`
 * Google Places powers the address typeahead; Smarty verifies it (both client-side,
 * keys via /api/config, fail-open). Our owned Tailwind twin of the prototype's
 * `RecipientDetails`.
 */
export default function RedemptionDetails({
  gift,
  product,
  variantId,
  customLocation = null,
  onEditLocation,
  shippingCountry,
  onBack,
  onRedeemed,
  onAlreadyRedeemed,
  initialDraft,
  onDraftChange,
}: RedemptionDetailsProps) {
  const { config } = gift;
  const redeem = useRedeemGift();
  // Client keys (Google Maps + Smarty) for the address flow, served via /api/config.
  const { data: publicConfig } = usePublicConfig();

  const [form, setForm] = useState<RecipientFormDraft>(
    () => initialDraft ?? { ...EMPTY_RECIPIENT_FORM, email: gift.recipient?.email ?? "" },
  );
  const [errors, setErrors] = useState<Set<string>>(() => new Set());
  const [phase, setPhase] = useState<Phase>("form");
  const [submitFailed, setSubmitFailed] = useState(false);
  // Smarty "did you mean?" modal data (null = closed).
  const [verifyModal, setVerifyModal] = useState<{
    currentLines: string[];
    suggestion?: AddressParts;
    message: string;
  } | null>(null);
  // Guards a stale spinner from resolving after the user edits / navigates away.
  const runId = useRef(0);

  // Envelope hand-off (RFW-8). `sending` reveals the phone-only card overlay that
  // rises then folds; `fading` runs the desktop staged cross-fade after the card
  // flies off. placingRef guards a double fold on a double-tap of the final Send.
  const [fading, setFading] = useState(false);
  const [sending, setSending] = useState(false);
  const placingRef = useRef(false);
  // The contact the redeem POST claimed (its id), handed to onRedeemed so the thank-you note can be
  // attributed — essential for a Universal Link, whose shared token binds to no single contact.
  const redeemedContactIdRef = useRef<number | undefined>(undefined);
  // The card giftFoldEnvelope folds: the visible summary card (desktop) or the
  // full-size card risen into the overlay (phone, where the summary is hidden).
  const summaryCardRef = useRef<HTMLDivElement>(null);
  const sendCardRef = useRef<HTMLDivElement>(null);

  // Report the draft on every change so the parent can both restore it across a "Back to gifts"
  // round-trip AND persist it for a browser refresh. A refresh tears the page down without running
  // React unmount cleanups, so the previous unmount-only sync lost any in-progress typing.
  useEffect(() => {
    onDraftChange?.(form);
  }, [form, onDraftChange]);

  // Move focus to the first invalid field once `errors` commits (aria-invalid is in
  // the DOM), landing SR + keyboard users on the error.
  const formElRef = useRef<HTMLDivElement>(null);
  const focusInvalidRef = useRef(false);
  useEffect(() => {
    if (!focusInvalidRef.current || errors.size === 0) return;
    focusInvalidRef.current = false;
    formElRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [errors]);

  const kind = giftKind(product);
  const needsShipping = kind === "physical";
  const isCharity = kind === "charity";
  const isExperience = kind === "experiences";

  const profile = getAddressProfile(shippingCountry);
  const { regionLabel, postalLabel } = profile;
  // Dropdown region options (states/provinces) come from country-state-city; free-text
  // countries get an empty list and render a text input instead.
  const regionOptions = useMemo(
    () => (profile.regionSelect ? getRegionOptions(shippingCountry) : []),
    [profile.regionSelect, shippingCountry],
  );
  const isUS = shippingCountry === "US";
  const countryEntry = AVAILABLE_COUNTRIES.find((c) => c.code === shippingCountry);
  const countryName = countryEntry?.name ?? shippingCountry;
  const countryFlag = countryEntry?.flag ?? "🌐";

  // Every gift type confirms before sending (RFW-8): the form CTA always advances
  // to the review, and the type-specific send action lives on the review screen.
  // (The form's section title + lead copy are derived inside RedemptionDetailsForm.)
  const formCtaLabel = "Continue";
  const confirmCtaLabel = needsShipping
    ? "Send my gift"
    : isCharity
      ? "Send my charity gift"
      : "Send my voucher";

  const setField = (key: keyof RecipientFormDraft, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors.has(key)) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const bad = new Set<string>();
    if (!form.firstName.trim()) bad.add("firstName");
    if (!form.lastName.trim()) bad.add("lastName");
    if (!form.email.trim() || !RECIPIENT_EMAIL_RE.test(form.email.trim())) bad.add("email");
    if (needsShipping) {
      // All shipping fields required except apartment/suite; region + postal
      // requirement follow the country's address profile.
      if (!form.address.trim()) bad.add("address");
      if (!form.city.trim()) bad.add("city");
      if (profile.regionRequired && !form.state.trim()) bad.add("state");
      if (profile.postalLabel && profile.postalRequired && !form.zipCode.trim()) {
        bad.add("zipCode");
      }
      if (!form.phone.trim() || !isValidPhone(form.phone, form.phoneCountry || shippingCountry)) {
        bad.add("phone");
      }
    }
    setErrors(bad);
    return bad.size === 0;
  };

  const buildRecipient = (): RecipientDetails => ({
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim(),
    phone: form.phone ? toE164(form.phone, form.phoneCountry || shippingCountry) : undefined,
    ...(needsShipping
      ? {
          address: form.address.trim(),
          apartment: form.apartment.trim() || undefined,
          city: form.city.trim(),
          state: form.state.trim(),
          zipCode: form.zipCode.trim(),
          country: countryName,
        }
      : {}),
  });

  const submit = async () => {
    setSubmitFailed(false);
    const id = ++runId.current;
    setPhase("submitting");
    try {
      // A short UX floor so the branded spinner doesn't flash on a fast network.
      const minSpin = new Promise((res) => setTimeout(res, 900));
      const [redeemResult] = await Promise.all([
        redeem.mutateAsync({
          token: gift.token,
          variantId,
          recipient: buildRecipient(),
          customLocation: customLocation ?? undefined,
        }),
        minSpin,
      ]);
      if (runId.current !== id) return;
      // Remember the claimed contact so the thank-you note (RFW-9) can be attributed to it — the
      // Universal Link's shared token has no bound contact, so this is the only handle to it.
      redeemedContactIdRef.current = redeemResult.contactId;
      // POST committed — run the fold-and-fly, which hands off to onRedeemed().
      runEnvelope();
    } catch (err) {
      if (runId.current !== id) return;
      // Spent after the page loaded: show the redeemed page instead of the generic failure.
      if (redemptionLinkError(err) === "already-redeemed") {
        onAlreadyRedeemed();
        return;
      }
      setSubmitFailed(true);
      // submit() is always reached from the review now, for every gift type.
      setPhase("review");
    }
  };

  /**
   * Fold-and-fly hand-off (RFW-8): once the redeem POST has committed, the chosen
   * gift's card folds into an envelope, seals, and flies off, then the surface
   * cross-fades to the confirmation. Ported from the prototype's `onPlaceOrder`.
   * Desktop folds the visible summary card and stages a body→header fade; phone
   * (where the summary card is hidden) rises a full-size card into an opaque
   * overlay, folds THAT, and lets the confirmation fade in over it. Reduced-motion
   * and the fold/seal/swoosh SFX are handled inside giftFoldEnvelope.
   */
  const runEnvelope = () => {
    if (placingRef.current) return;
    placingRef.current = true;
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isMobile && !reduced) {
      // Summary card is display:none on phone: rise a full-size card into the
      // opaque overlay (over the spinner), then fold it. The overlay stays opaque
      // so the confirmation fades in over it with no flash of the form behind.
      setSending(true);
      // Wait out the 0.5s card rise (+ a settle beat) before folding.
      window.setTimeout(() => {
        giftFoldEnvelope(sendCardRef.current, {
          envelopeColor: config.envelopeColor,
          onComplete: () => window.setTimeout(() => onRedeemed(redeemedContactIdRef.current), 150),
        });
      }, 820);
      return;
    }

    // Desktop / reduced-motion: KEEP the loading spinner up (do NOT return to the
    // editable review — the user already confirmed and the POST has committed, so
    // re-offering "Edit" would be wrong). The spinner holds in the form column
    // while the visible summary card folds beside it, then the body→header fade.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        giftFoldEnvelope(summaryCardRef.current, {
          envelopeColor: config.envelopeColor,
          onComplete: () => {
            setFading(true);
            window.setTimeout(() => onRedeemed(redeemedContactIdRef.current), 820);
          },
        });
      }),
    );
  };

  // Google/Smarty return the region short code (e.g. "CA"); the region <select> is
  // keyed by the full name ("California"), so normalize it or the field stays blank.
  const applyAddress = (p: AddressParts) =>
    setForm((f) => ({
      ...f,
      address: p.street,
      city: p.city,
      state: matchRegionValue(regionOptions, p.state),
      zipCode: p.zip,
    }));

  /**
   * Smarty US-address verification (runs on Continue for US physical gifts): a spinner
   * while validating → `verified` proceeds using Smarty's canonical form → `corrected`
   * / `unverified` opens the "did you mean?" modal → `error` proceeds silently. Never
   * blocks (fail-open), and `runId` discards a stale result if the user navigates away.
   */
  const runVerify = async () => {
    const id = ++runId.current;
    setPhase("verifying");
    const typed = {
      address: form.address.trim(),
      apartment: form.apartment.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zipCode: form.zipCode.trim(),
    };
    // Short floor so the spinner reads as intentional on a fast network.
    const minSpin = new Promise((res) => setTimeout(res, 700));
    const [result] = await Promise.all([
      verifyUsAddress(typed, publicConfig?.smartyApiKey),
      minSpin,
    ]);
    if (runId.current !== id) return;
    if (result.status === "verified") {
      if (result.suggestion) applyAddress(result.suggestion);
      setPhase("review");
    } else if (result.status === "corrected" && result.suggestion) {
      setVerifyModal({
        currentLines: addressInputLines(typed),
        suggestion: result.suggestion,
        message: "We found a suggested correction — use it, or keep the address you entered.",
      });
    } else if (result.status === "unverified") {
      setVerifyModal({
        currentLines: addressInputLines(typed),
        message: "We couldn't verify this address. Please double-check it, or use it as entered.",
      });
    } else {
      setPhase("review"); // error → fail-open, proceed silently
    }
  };

  // Form primary action: US physical gifts verify the address first (spinner →
  // feedback), then land on the review; everything else goes straight to review.
  const onPrimary = () => {
    if (!validate()) {
      focusInvalidRef.current = true;
      return;
    }
    if (needsShipping && isUS) runVerify();
    else setPhase("review");
  };

  // Verify-modal actions.
  const onVerifyUseSuggested = () => {
    if (verifyModal?.suggestion) applyAddress(verifyModal.suggestion);
    setVerifyModal(null);
    setPhase("review");
  };
  const onVerifyUseAsIs = () => {
    setVerifyModal(null);
    setPhase("review");
  };
  const onVerifyEdit = () => {
    setVerifyModal(null);
    setPhase("form");
  };

  const onEdit = (which: "email" | "address") => {
    runId.current++; // cancel any in-flight spinner
    setPhase("form");
    const id = which === "address" ? "iv-address" : "iv-email";
    setTimeout(() => document.getElementById(id)?.focus(), 60);
  };

  const reviewAddressLines = buildReviewAddressLines(form, {
    regions: regionOptions,
    postalBeforeCity: profile.postalBeforeCity ?? false,
    countryName,
    isUS,
  });

  return (
    <div
      data-scroll-lock
      // 16px inputs on phones so iOS Safari doesn't zoom-on-focus (prototype's
      // `.iv-view input/select { font-size: 16px }` ≤640px guard).
      className="absolute inset-0 flex flex-col overflow-y-auto bg-white font-lato max-sm:[&_input]:text-base max-sm:[&_select]:text-base"
    >
      <RedemptionDetailsHeader
        product={product}
        onBack={onBack}
        partnerLogoUrl={config.showLogo ? config.logoUrl : undefined}
        // Staged cross-fade: the header fades a beat AFTER the body (delay-400)
        // once the envelope has flown off. The white root stays put underneath.
        className={`transition-opacity delay-400 duration-400 ${fading ? "opacity-0" : "opacity-100"}`}
      />

      {/* Split body — white form + clear-dust summary. Fades first in the hand-off. */}
      <div
        className={`grid flex-1 grid-cols-1 transition-opacity duration-400 md:grid-cols-2 ${fading ? "opacity-0" : "opacity-100"}`}
      >
        {/* Form column */}
        <div className="relative flex justify-center bg-white px-4.5 pt-6 pb-28 sm:px-5.5 sm:pb-12 md:justify-end md:px-14 md:pt-14 md:pb-18">
          <div className="w-full max-w-120" ref={formElRef}>
            {phase === "form" && (
              <RedemptionDetailsForm
                form={form}
                errors={errors}
                setField={setField}
                onAddressSelect={applyAddress}
                needsShipping={needsShipping}
                isCharity={isCharity}
                isExperience={isExperience}
                isUS={isUS}
                shippingCountry={shippingCountry}
                countryName={countryName}
                countryFlag={countryFlag}
                regionLabel={regionLabel}
                regionOptions={regionOptions}
                postalLabel={postalLabel}
                submitFailed={submitFailed}
                ctaLabel={formCtaLabel}
                onPrimary={onPrimary}
              />
            )}

            {phase === "review" && (
              <div className="flex flex-col items-start">
                <h2 className="mb-4.5 font-crimson text-[22px] font-normal text-rodeo-black sm:text-[32px]">
                  {isCharity ? "Charity Confirmation" : "Check your details before we send"}
                </h2>
                <p className="mb-5.5 max-w-110 font-lato text-[15px] leading-[1.55] text-graphite">
                  Just make sure everything's right, then we'll send your gift on its way.
                </p>
                <RedemptionReviewCard
                  email={form.email}
                  addressLines={reviewAddressLines}
                  needsShipping={needsShipping}
                  customLocation={customLocation}
                  onEditEmail={() => onEdit("email")}
                  onEditAddress={() => onEdit("address")}
                  onEditLocation={onEditLocation}
                />
                {submitFailed && (
                  <p className="mt-4 font-lato text-[13px] text-good-wine">
                    Something went wrong sending your gift. Please try again.
                  </p>
                )}
                {/* Desktop review CTA; phone uses the sticky shelf pill (hidden here). */}
                <GfgButton
                  variant="primary"
                  className={`mt-8.5 hidden sm:inline-flex ${CTA_SIZE}`}
                  onClick={submit}
                  endIcon={<Send className={SEND_ICON} sx={{ fontSize: 17 }} />}
                >
                  {confirmCtaLabel}
                </GfgButton>
              </div>
            )}
          </div>

          {/* Branded processing spinner — the address-verify wait + the submit wait.
              Covers the form column on ≥sm, the full viewport on phone. */}
          {phase === "submitting" && <RedemptionSpinner label="Submitting your details…" />}
          {phase === "verifying" && !verifyModal && (
            <RedemptionSpinner label="Checking your address…" />
          )}
        </div>

        {/* Summary column — hidden on phone; on top for sm–md, sticky right at md+
            (two-column split matches the prototype's ~820px threshold). */}
        <div className="order-first hidden justify-center bg-clear-dust sm:flex sm:px-5.5 sm:pt-8 sm:pb-6 md:order-0 md:items-start md:border-card-border md:border-l md:px-14 md:py-14">
          <div ref={summaryCardRef} className="w-full max-w-95 md:sticky md:top-14">
            <ProductCard product={product} interactive={false} />
          </div>
        </div>
      </div>

      {/* Phone-only envelope hand-off: the summary card is hidden on phone, so a
          full-size card rises in an opaque overlay (over the spinner) and then
          giftFoldEnvelope folds + flies it. The overlay stays opaque through the
          hand-off so the confirmation fades in over it. */}
      {sending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white p-6 motion-safe:animate-send-card-in sm:hidden">
          <div ref={sendCardRef} className="w-full max-w-80">
            <ProductCard product={product} interactive={false} />
          </div>
        </div>
      )}

      {/* Sticky bottom CTA shelf — phone only. Carries the active phase's primary
          action; hidden during the full-screen spinner (verify + submit). */}
      {phase !== "submitting" && phase !== "verifying" && (
        <RedemptionCtaShelf
          label={phase === "review" ? confirmCtaLabel : formCtaLabel}
          endIcon={
            phase === "review" ? (
              <Send className={SEND_ICON} sx={{ fontSize: 17 }} />
            ) : (
              <ArrowForward sx={{ fontSize: 18 }} />
            )
          }
          onClick={phase === "review" ? submit : onPrimary}
        />
      )}

      {/* Smarty address-verification "did you mean?" dialog. */}
      {verifyModal && (
        <RedemptionAddressVerifyModal
          currentLines={verifyModal.currentLines}
          suggestion={verifyModal.suggestion}
          message={verifyModal.message}
          onUseSuggested={onVerifyUseSuggested}
          onUseAsIs={onVerifyUseAsIs}
          onEdit={onVerifyEdit}
        />
      )}
    </div>
  );
}
